import {
  AnnouncementStatusBadge,
  OrganizationStatusBadge
} from './status-badge'
import {
  ANNOUNCEMENT_TYPE_LABELS,
  formatDateTime,
  formatGermanDate
} from './utils'

import type {
  AnnouncementOrganizationStatus,
  AnnouncementSummary,
  AnnouncementWithSummary
} from '@/api/announcements-client'
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header'
import { TableColumn } from '@/models'

const SummaryCounts = ({ summary }: { summary?: AnnouncementSummary }) => {
  if (!summary) {
    return <span className="text-muted-foreground">–</span>
  }

  return (
    <span className="whitespace-nowrap text-xs">
      <span title="Acknowledged / eligible">
        {summary.acknowledged}/{summary.eligible} ack
      </span>
      <span className="text-muted-foreground">
        {' '}
        · {summary.pending} pending · {summary.expired} expired ·{' '}
        {summary.superseded} superseded
      </span>
    </span>
  )
}

export const getAnnouncementTableColumns =
  (): TableColumn<AnnouncementWithSummary>[] => [
    {
      id: 'type',
      accessorFn: (row) => ANNOUNCEMENT_TYPE_LABELS[row.type] ?? row.type,
      displayName: 'Type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      enableColumnFilter: false
    },
    {
      id: 'list_version',
      accessorKey: 'list_version',
      displayName: 'List version',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="List version" />
      ),
      enableColumnFilter: false
    },
    {
      id: 'effective_date',
      accessorKey: 'effective_date',
      displayName: 'Effective date',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Effective date" />
      ),
      cell: ({ row }) => formatGermanDate(row.original.effective_date, '–'),
      enableColumnFilter: false
    },
    {
      id: 'status',
      accessorKey: 'status',
      displayName: 'Status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => (
        <AnnouncementStatusBadge status={row.original.status} />
      ),
      enableColumnFilter: false
    },
    {
      id: 'published_at',
      accessorKey: 'published_at',
      displayName: 'Published at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Published at" />
      ),
      cell: ({ row }) => formatDateTime(row.original.published_at),
      enableGlobalFilter: false,
      enableColumnFilter: false
    },
    {
      id: 'expires_at',
      accessorKey: 'expires_at',
      displayName: 'Expires at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Expires at" />
      ),
      cell: ({ row }) => formatDateTime(row.original.expires_at),
      enableGlobalFilter: false,
      enableColumnFilter: false
    },
    {
      id: 'summary',
      accessorFn: (row) =>
        row.summary
          ? `${row.summary.acknowledged}/${row.summary.eligible}`
          : '',
      displayName: 'Summary',
      header: () => <span>Summary</span>,
      cell: ({ row }) =>
        row.original.id ? (
          <SummaryCounts summary={row.original.summary} />
        ) : null,
      enableSorting: false,
      enableGlobalFilter: false,
      enableColumnFilter: false
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      displayName: 'Created at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created at" />
      ),
      cell: ({ row }) => formatDateTime(row.original.created_at),
      enableGlobalFilter: false,
      enableColumnFilter: false
    }
  ]

export const getAnnouncementOrganizationColumns = (
  orgNames: Map<string, string>
): TableColumn<AnnouncementOrganizationStatus>[] => [
  {
    id: 'org_id',
    accessorKey: 'org_id',
    displayName: 'Org ID',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Org ID" />
    ),
    enableColumnFilter: false
  },
  {
    id: 'org_name',
    accessorFn: (row) => row.org_name || orgNames.get(row.org_id) || '',
    displayName: 'Organization',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organization" />
    ),
    enableColumnFilter: false
  },
  {
    id: 'status',
    accessorKey: 'status',
    displayName: 'Status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <OrganizationStatusBadge status={row.original.status} />,
    enableGlobalFilter: false,
    enableColumnFilter: false
  },
  {
    id: 'acknowledged_by_name',
    accessorFn: (row) => row.acknowledged_by?.name ?? '',
    displayName: 'Acknowledged by',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Acknowledged by" />
    ),
    enableGlobalFilter: false,
    enableColumnFilter: false
  },
  {
    id: 'acknowledged_by_email',
    accessorFn: (row) => row.acknowledged_by?.email ?? '',
    displayName: 'Acknowledged by (email)',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    enableGlobalFilter: false,
    enableColumnFilter: false
  },
  {
    id: 'acknowledged_by_user_id',
    accessorFn: (row) => row.acknowledged_by?.user_id ?? '',
    displayName: 'Acknowledged by (user ID)',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="User ID" />
    ),
    enableGlobalFilter: false,
    enableColumnFilter: false
  },
  {
    id: 'acknowledged_at',
    accessorKey: 'acknowledged_at',
    displayName: 'Acknowledged at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Acknowledged at" />
    ),
    cell: ({ row }) => formatDateTime(row.original.acknowledged_at),
    enableGlobalFilter: false,
    enableColumnFilter: false
  },
  {
    id: 'portal',
    accessorKey: 'portal',
    displayName: 'Portal',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Portal" />
    ),
    enableGlobalFilter: false,
    enableColumnFilter: false
  }
]
